import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { session } from './auth'
import { offsetAt } from './orgClock'

/**
 * Formatting of dates, times, durations, numbers and amounts. The language
 * comes from the UI; the timezone and currency from the org (the browser's
 * timezone is irrelevant: a call window closes at the org's local time). The
 * locale is language + org country: `ar-MA` writes Latin digits, as Morocco
 * does. Every function returns '—' for a missing or unreadable value.
 */
export interface FormatContext {
  locale: string
  currency: string
  /**
   * The org's UTC offset at an instant, in minutes (lib/orgClock.ts): the
   * screens never convert with the browser's tz data, which may be older
   * than the org's rules. `timeZone` is only a fallback (tests, no session).
   */
  offsetAt?: (t: number) => number
  timeZone?: string
}

const DASH = '—'
type Stamp = string | Date | null | undefined

/**
 * Reads a PostgreSQL timestamp: "2026-09-22 11:32:48.123+00" (space instead of
 * T, offset without minutes), which `new Date()` rejects silently. A bare date
 * ("2026-10-15") is a calendar day, not an instant: see `isDateOnly`.
 */
export function parseTimestamp(v: Stamp): Date | null {
  if (!v) return null
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (day) return new Date(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]), 12))
  const withT = v.replace(' ', 'T')
  const iso = /T\d{2}:\d{2}/.test(withT) ? withT.replace(/([+-]\d{2})$/, '$1:00') : withT
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

const isDateOnly = (v: Stamp): boolean => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)

export function formatters(ctx: FormatContext) {
  const dt = (v: Stamp, o: Intl.DateTimeFormatOptions) => {
    const d = parseTimestamp(v)
    if (!d) return DASH
    // A calendar day is shown as is, never shifted by a timezone.
    if (isDateOnly(v)) return new Intl.DateTimeFormat(ctx.locale, { ...o, timeZone: 'UTC' }).format(d)
    // An instant: moved by the org's offset, then read as UTC.
    if (ctx.offsetAt) {
      const moved = new Date(d.getTime() + ctx.offsetAt(d.getTime()) * 60_000)
      return new Intl.DateTimeFormat(ctx.locale, { ...o, timeZone: 'UTC' }).format(moved)
    }
    return new Intl.DateTimeFormat(ctx.locale, { ...o, timeZone: ctx.timeZone }).format(d)
  }
  const num = (n: number | null | undefined, o: Intl.NumberFormatOptions = {}) =>
    n == null || !Number.isFinite(n) ? DASH : new Intl.NumberFormat(ctx.locale, o).format(n)

  return {
    date: (v: Stamp) => dt(v, { dateStyle: 'short' }),
    dateLong: (v: Stamp) => dt(v, { dateStyle: 'long' }),
    time: (v: Stamp) => dt(v, { hour: '2-digit', minute: '2-digit' }),
    timeSeconds: (v: Stamp) => dt(v, { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    dateTime: (v: Stamp) => dt(v, { dateStyle: 'short', timeStyle: 'short' }),
    /** Date and time with seconds (an audit line, a call timeline). */
    dateTimeSeconds: (v: Stamp) => dt(v, { dateStyle: 'short', timeStyle: 'medium' }),
    weekday: (v: Stamp) => dt(v, { weekday: 'long' }),
    /** "vendredi 25 septembre": a day heading. */
    dayLong: (v: Stamp) => dt(v, { weekday: 'long', day: 'numeric', month: 'long' }),
    /** "25 sept.": a short day for charts and lists. */
    dayShort: (v: Stamp) => dt(v, { day: 'numeric', month: 'short' }),
    /** "septembre 2026". */
    month: (v: Stamp) => dt(v, { month: 'long', year: 'numeric' }),
    /** Any Intl date options, in the org's time. */
    custom: (v: Stamp, o: Intl.DateTimeFormatOptions) => dt(v, o),

    /** "il y a 4 min", "dans 2 jours": the exact time stays shown next to it. */
    relative(v: Stamp, now = new Date()): string {
      const d = parseTimestamp(v)
      if (!d) return DASH
      const s = Math.round((d.getTime() - now.getTime()) / 1000)
      const rtf = new Intl.RelativeTimeFormat(ctx.locale, { numeric: 'auto' })
      const steps: Array<[number, Intl.RelativeTimeFormatUnit]> = [
        [60, 'second'], [3600, 'minute'], [86400, 'hour'], [86400 * 30, 'day'],
        [86400 * 365, 'month'], [Infinity, 'year'],
      ]
      const divisors: Record<string, number> = {
        second: 1, minute: 60, hour: 3600, day: 86400, month: 86400 * 30, year: 86400 * 365,
      }
      const [, unit] = steps.find(([limit]) => Math.abs(s) < limit)!
      return rtf.format(Math.round(s / divisors[unit]!), unit)
    },

    /** h:mm:ss, or m:ss under an hour: a supervisor reads a time, not a count of seconds. */
    duration(seconds: number | null | undefined): string {
      if (seconds == null || !Number.isFinite(seconds)) return DASH
      const s = Math.max(0, Math.floor(seconds))
      const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
      return (h ? `${h}:${String(m).padStart(2, '0')}` : String(m)) + ':' + String(s % 60).padStart(2, '0')
    },

    number: (n: number | null | undefined, digits = 0) =>
      num(n, { maximumFractionDigits: digits, minimumFractionDigits: 0 }),
    /** `ratio` is a fraction (0.25 → 25 %). */
    percent: (ratio: number | null | undefined, digits = 0) =>
      num(ratio, { style: 'percent', maximumFractionDigits: digits }),
    /**
     * A price without its currency, with at least two decimals and up to `digits`
     * (a per-minute price of 0.0315 keeps its four): an exact string from the API
     * is only shown, never computed.
     */
    price: (amount: number | string | null | undefined, digits = 4) => {
      const n = amount == null || amount === '' ? null : Number(amount)
      return num(n, { minimumFractionDigits: 2, maximumFractionDigits: Math.max(2, digits) })
    },
    /** An unknown or empty currency code never breaks a table: the amount then shows with the code as given. */
    money: (amount: number | string | null | undefined, currency = ctx.currency) => {
      const n = amount == null || amount === '' ? null : Number(amount)
      try {
        return num(n, { style: 'currency', currency })
      } catch {
        const plain = num(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        return plain === DASH || !currency ? plain : `${plain} ${currency}`
      }
    },
  }
}

export type Formatters = ReturnType<typeof formatters>

/**
 * Intl locale for a UI language and an org country. English outside the
 * United States writes the day first and a 24-hour clock (en-GB): `en-MA`
 * would fall back to US formats (9/24/26, 3:30 PM) in a Moroccan call centre.
 */
export function formatLocale(language: string, country: string): string {
  if (language === 'en' && country !== 'US') return 'en-GB'
  return `${language}-${country}`
}

/** Formatters for the current language and the session's org. */
export function useFormat() {
  const { locale } = useI18n()
  const f = computed(() => formatters({
    locale: formatLocale(locale.value, session.org?.country ?? 'MA'),
    offsetAt,
    timeZone: session.org?.timezone,
    currency: session.org?.currency ?? 'MAD',
  }))
  return new Proxy({} as Formatters, { get: (_t, k) => f.value[k as keyof Formatters] })
}
